import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBListManager from "./DBListManager";
import DBStringUtils from "./DBStringUtils";

class DBTemplateManager {

    //#region Clear All

    public static ClearAll = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.Lists.length === 0) { return; }
            DBTemplateManager.ClearAllConfirmed();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.ClearAll");
        }
    };

    private static ClearAllConfirmed = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.Lists = [];
            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.ShowToast("All lists have been cleared", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.ClearAllConfirmed");
        }
    };

    //#endregion

    //#region Load Example Data

    public static LoadExampleData = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBTemplateManager.LoadExampleDataConfirmed();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.LoadExampleData");
        }
    };

    private static LoadExampleDataConfirmed = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.FieldIdCounter = 0;
            const appCode: string = DBState.GetAppCode();
            DBState.Lists = [
                DBTemplateManager.BuildCategoriesList(appCode),
                DBTemplateManager.BuildSuppliersList(appCode),
                DBTemplateManager.BuildProductsList(appCode),
                DBTemplateManager.BuildCustomersList(appCode),
                DBTemplateManager.BuildOrdersList(appCode),
                DBTemplateManager.BuildOrderItemsList(appCode),
                DBTemplateManager.BuildProductReviewsList(appCode)
            ];
            DBState.SaveState();
            DBListManager.AutoArrangeLists();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.LoadExampleDataConfirmed");
        }
    };

    //#endregion

    //#region Build Categories List

    public static BuildCategoriesList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "CategoryName"] = {
                Title: "Category Name",
                Type: "string",
                Format: "",
                Description: "Name of the product category",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "CategoryDescription"] = {
                Title: "Category Description",
                Type: "string",
                Format: "note",
                Description: "Detailed description of the category"
            } as IFieldDefinition;
            properties[appCode + "CategoryImage"] = {
                Title: "Category Image",
                Type: "string",
                Format: "url",
                Description: "URL of the category image"
            } as IFieldDefinition;
            properties[appCode + "IsActive"] = {
                Title: "Is Active",
                Type: "boolean",
                Description: "Whether the category is currently active",
                DefaultValue: "true"
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Categories",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildCategoriesList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Build Suppliers List

    public static BuildSuppliersList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "SupplierName"] = {
                Title: "Supplier Name",
                Type: "string",
                Description: "Name of the supplier company",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ContactPerson"] = {
                Title: "Contact Person",
                Type: "string",
                Description: "Primary contact person at the supplier"
            } as IFieldDefinition;
            properties[appCode + "Email"] = {
                Title: "Email",
                Type: "string",
                Format: "email",
                Description: "Contact email address"
            } as IFieldDefinition;
            properties[appCode + "Phone"] = {
                Title: "Phone",
                Type: "string",
                Description: "Contact phone number"
            } as IFieldDefinition;
            properties[appCode + "Address"] = {
                Title: "Address",
                Type: "string",
                Format: "note",
                Description: "Supplier business address"
            } as IFieldDefinition;
            properties[appCode + "Country"] = {
                Title: "Country",
                Type: "string",
                Description: "Country of the supplier"
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Suppliers",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildSuppliersList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Build Products List

    public static BuildProductsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = DBTemplateManager.BuildProductsProperties(appCode);
            const data: Record<string, unknown>[] = DBTemplateManager.BuildProductsData(appCode);
            const views: unknown[] = DBTemplateManager.BuildProductsViews(appCode);
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Products",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: data,
                Views: views,
                Settings: {
                    ContentTypesEnabled: true,
                    EnableVersioning: true
                },
                Position: { X: 750, Y: 50 },
                Collapsed: false,
                UseContentType: true,
                ContentTypeName: "CTProduct",
                ContentTypeGroup: "Custom Content Types"
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProductsList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildProductsProperties = (appCode: string): Record<string, IFieldDefinition> => {
        if (ks.kErrorMode === true) { return {}; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ProductName"] = {
                Title: "Product Name",
                Type: "string",
                Description: "Name of the product",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ProductDescription"] = {
                Title: "Product Description",
                Type: "string",
                Format: "note",
                Description: "Detailed product description"
            } as IFieldDefinition;
            properties[appCode + "SKU"] = {
                Title: "SKU",
                Type: "string",
                Description: "Stock Keeping Unit identifier",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Price"] = {
                Title: "Price",
                Type: "number",
                Description: "Product price",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "StockQuantity"] = {
                Title: "Stock Quantity",
                Type: "integer",
                Description: "Current stock quantity"
            } as IFieldDefinition;
            properties[appCode + "Category"] = {
                Title: "Category",
                Type: "lookup",
                Description: "Product category",
                LookupList: "Categories",
                LookupField: appCode + "CategoryName"
            } as IFieldDefinition;
            properties[appCode + "Supplier"] = {
                Title: "Supplier",
                Type: "lookup",
                Description: "Product supplier",
                LookupList: "Suppliers",
                LookupField: appCode + "SupplierName"
            } as IFieldDefinition;
            properties[appCode + "IsAvailable"] = {
                Title: "Is Available",
                Type: "boolean",
                Description: "Whether the product is available for purchase",
                DefaultValue: "true"
            } as IFieldDefinition;
            return properties;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProductsProperties");
            return {};
        }
    };

    private static BuildProductsData = (appCode: string): Record<string, unknown>[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const row1: Record<string, unknown> = {};
            row1[appCode + "ProductName"] = "Wireless Bluetooth Headphones";
            row1[appCode + "ProductDescription"] = "Premium noise-cancelling wireless headphones";
            row1[appCode + "SKU"] = "WBH-001";
            row1[appCode + "Price"] = 79.99;
            row1[appCode + "StockQuantity"] = 150;
            row1[appCode + "IsAvailable"] = true;

            const row2: Record<string, unknown> = {};
            row2[appCode + "ProductName"] = "USB-C Charging Cable";
            row2[appCode + "ProductDescription"] = "Fast charging braided cable, 6ft";
            row2[appCode + "SKU"] = "UCC-002";
            row2[appCode + "Price"] = 12.99;
            row2[appCode + "StockQuantity"] = 500;
            row2[appCode + "IsAvailable"] = true;

            const row3: Record<string, unknown> = {};
            row3[appCode + "ProductName"] = "Laptop Stand";
            row3[appCode + "ProductDescription"] = "Adjustable aluminum laptop stand";
            row3[appCode + "SKU"] = "LS-003";
            row3[appCode + "Price"] = 45.00;
            row3[appCode + "StockQuantity"] = 8;
            row3[appCode + "IsAvailable"] = true;

            return [row1, row2, row3];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProductsData");
            return [];
        }
    };

    private static BuildProductsViews = (appCode: string): unknown[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const allProductsView: Record<string, unknown> = {
                Id: "view_" + (++DBState.FieldIdCounter),
                Title: "All Products",
                IsDefault: true,
                ViewFields: [
                    appCode + "ProductName",
                    appCode + "SKU",
                    appCode + "Price",
                    appCode + "StockQuantity",
                    appCode + "Category",
                    appCode + "IsAvailable"
                ],
                ViewQuery: "",
                Aggregations: []
            };
            const lowStockView: Record<string, unknown> = {
                Id: "view_" + (++DBState.FieldIdCounter),
                Title: "Low Stock Items",
                IsDefault: false,
                ViewFields: [
                    appCode + "ProductName",
                    appCode + "SKU",
                    appCode + "StockQuantity",
                    appCode + "Supplier",
                    appCode + "IsAvailable"
                ],
                ViewQuery: "<Where><Lt><FieldRef Name='" + appCode + "StockQuantity'/><Value Type='Integer'>20</Value></Lt></Where>",
                Aggregations: [
                    { FieldName: appCode + "StockQuantity", Function: "SUM" }
                ]
            };
            return [allProductsView, lowStockView];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProductsViews");
            return [];
        }
    };

    //#endregion

    //#region Build Customers List

    public static BuildCustomersList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "CustomerName"] = {
                Title: "Customer Name",
                Type: "string",
                Description: "Full name of the customer",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "CustomerEmail"] = {
                Title: "Customer Email",
                Type: "string",
                Format: "email",
                Description: "Customer email address",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "CustomerPhone"] = {
                Title: "Customer Phone",
                Type: "string",
                Description: "Customer phone number"
            } as IFieldDefinition;
            properties[appCode + "ShippingAddress"] = {
                Title: "Shipping Address",
                Type: "string",
                Format: "note",
                Description: "Default shipping address"
            } as IFieldDefinition;
            properties[appCode + "MemberSince"] = {
                Title: "Member Since",
                Type: "string",
                Format: "date",
                Description: "Date the customer joined"
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Customers",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 350 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildCustomersList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Build Orders List

    public static BuildOrdersList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "OrderNumber"] = {
                Title: "Order Number",
                Type: "string",
                Description: "Unique order identifier",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Customer"] = {
                Title: "Customer",
                Type: "lookup",
                Description: "Customer who placed the order",
                LookupList: "Customers",
                LookupField: appCode + "CustomerName",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "OrderDate"] = {
                Title: "Order Date",
                Type: "string",
                Format: "date",
                Description: "Date the order was placed",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "OrderStatus"] = {
                Title: "Order Status",
                Type: "string",
                Description: "Current status of the order",
                Enum: ["Pending", "Processing", "Shipped", "Delivered", "Cancelled"]
            } as IFieldDefinition;
            properties[appCode + "TotalAmount"] = {
                Title: "Total Amount",
                Type: "number",
                Description: "Total order amount"
            } as IFieldDefinition;
            properties[appCode + "ShippingMethod"] = {
                Title: "Shipping Method",
                Type: "string",
                Description: "Selected shipping method",
                Enum: ["Standard", "Express", "Overnight"]
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Orders",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 350 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildOrdersList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Build Order Items List

    public static BuildOrderItemsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "Order"] = {
                Title: "Order",
                Type: "lookup",
                Description: "Parent order",
                LookupList: "Orders",
                LookupField: appCode + "OrderNumber",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Product"] = {
                Title: "Product",
                Type: "lookup",
                Description: "Product in this line item",
                LookupList: "Products",
                LookupField: appCode + "ProductName",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Quantity"] = {
                Title: "Quantity",
                Type: "integer",
                Description: "Number of units ordered",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "UnitPrice"] = {
                Title: "Unit Price",
                Type: "number",
                Description: "Price per unit at time of order"
            } as IFieldDefinition;
            properties[appCode + "LineTotal"] = {
                Title: "Line Total",
                Type: "number",
                Description: "Total for this line item"
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "OrderItems",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 750, Y: 350 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildOrderItemsList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Build Product Reviews List

    public static BuildProductReviewsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ReviewProduct"] = {
                Title: "Product",
                Type: "lookup",
                Description: "Product being reviewed",
                LookupList: "Products",
                LookupField: appCode + "ProductName",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Reviewer"] = {
                Title: "Reviewer",
                Type: "lookup",
                Description: "Customer who wrote the review",
                LookupList: "Customers",
                LookupField: appCode + "CustomerName"
            } as IFieldDefinition;
            properties[appCode + "Rating"] = {
                Title: "Rating",
                Type: "integer",
                Description: "Rating from 1 to 5",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ReviewTitle"] = {
                Title: "Review Title",
                Type: "string",
                Description: "Title of the review"
            } as IFieldDefinition;
            properties[appCode + "ReviewText"] = {
                Title: "Review Text",
                Type: "string",
                Format: "note",
                Description: "Full review text"
            } as IFieldDefinition;
            properties[appCode + "ReviewDate"] = {
                Title: "Review Date",
                Type: "string",
                Format: "date",
                Description: "Date the review was submitted"
            } as IFieldDefinition;
            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "ProductReviews",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 650 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
            return list;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProductReviewsList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Built-In Templates

    public static GetEcommerceTemplate = (): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const appCode: string = DBState.GetAppCode();
            const categories: IListDefinition = DBTemplateManager.BuildSimpleEcommerceCategories(appCode);
            const products: IListDefinition = DBTemplateManager.BuildSimpleEcommerceProducts(appCode);
            const customers: IListDefinition = DBTemplateManager.BuildSimpleEcommerceCustomers(appCode);
            return [categories, products, customers];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetEcommerceTemplate");
            return [];
        }
    };

    private static BuildSimpleEcommerceCategories = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "CategoryName"] = {
                Title: "Category Name",
                Type: "string",
                Description: "Name of the category",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "CategoryDescription"] = {
                Title: "Description",
                Type: "string",
                Format: "note",
                Description: "Category description"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Categories",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildSimpleEcommerceCategories");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildSimpleEcommerceProducts = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ProductName"] = {
                Title: "Product Name",
                Type: "string",
                Description: "Name of the product",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Price"] = {
                Title: "Price",
                Type: "number",
                Description: "Product price"
            } as IFieldDefinition;
            properties[appCode + "Category"] = {
                Title: "Category",
                Type: "lookup",
                Description: "Product category",
                LookupList: "Categories",
                LookupField: appCode + "CategoryName"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Products",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildSimpleEcommerceProducts");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildSimpleEcommerceCustomers = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "CustomerName"] = {
                Title: "Customer Name",
                Type: "string",
                Description: "Full name",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Email"] = {
                Title: "Email",
                Type: "string",
                Format: "email",
                Description: "Email address"
            } as IFieldDefinition;
            properties[appCode + "Phone"] = {
                Title: "Phone",
                Type: "string",
                Description: "Phone number"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Customers",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 750, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildSimpleEcommerceCustomers");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Project Management Template

    public static GetProjectManagementTemplate = (): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const appCode: string = DBState.GetAppCode();
            const projects: IListDefinition = DBTemplateManager.BuildProjectsList(appCode);
            const tasks: IListDefinition = DBTemplateManager.BuildTasksList(appCode);
            return [projects, tasks];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetProjectManagementTemplate");
            return [];
        }
    };

    private static BuildProjectsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ProjectName"] = {
                Title: "Project Name",
                Type: "string",
                Description: "Name of the project",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ProjectDescription"] = {
                Title: "Description",
                Type: "string",
                Format: "note",
                Description: "Project description"
            } as IFieldDefinition;
            properties[appCode + "StartDate"] = {
                Title: "Start Date",
                Type: "string",
                Format: "date",
                Description: "Project start date"
            } as IFieldDefinition;
            properties[appCode + "EndDate"] = {
                Title: "End Date",
                Type: "string",
                Format: "date",
                Description: "Project end date"
            } as IFieldDefinition;
            properties[appCode + "Status"] = {
                Title: "Status",
                Type: "string",
                Description: "Current project status",
                Enum: ["Not Started", "In Progress", "On Hold", "Completed", "Cancelled"]
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Projects",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildProjectsList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildTasksList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "TaskName"] = {
                Title: "Task Name",
                Type: "string",
                Description: "Name of the task",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Project"] = {
                Title: "Project",
                Type: "lookup",
                Description: "Parent project",
                LookupList: "Projects",
                LookupField: appCode + "ProjectName",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "AssignedTo"] = {
                Title: "Assigned To",
                Type: "string",
                Description: "Person assigned to this task"
            } as IFieldDefinition;
            properties[appCode + "DueDate"] = {
                Title: "Due Date",
                Type: "string",
                Format: "date",
                Description: "Task due date"
            } as IFieldDefinition;
            properties[appCode + "Priority"] = {
                Title: "Priority",
                Type: "string",
                Description: "Task priority level",
                Enum: ["Low", "Medium", "High", "Critical"]
            } as IFieldDefinition;
            properties[appCode + "TaskStatus"] = {
                Title: "Status",
                Type: "string",
                Description: "Current task status",
                Enum: ["To Do", "In Progress", "In Review", "Done"]
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Tasks",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildTasksList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region CRM Template

    public static GetCRMTemplate = (): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const appCode: string = DBState.GetAppCode();
            const companies: IListDefinition = DBTemplateManager.BuildCompaniesList(appCode);
            const contacts: IListDefinition = DBTemplateManager.BuildContactsList(appCode);
            const opportunities: IListDefinition = DBTemplateManager.BuildOpportunitiesList(appCode);
            return [companies, contacts, opportunities];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetCRMTemplate");
            return [];
        }
    };

    private static BuildCompaniesList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "CompanyName"] = {
                Title: "Company Name",
                Type: "string",
                Description: "Name of the company",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Industry"] = {
                Title: "Industry",
                Type: "string",
                Description: "Industry sector"
            } as IFieldDefinition;
            properties[appCode + "Website"] = {
                Title: "Website",
                Type: "string",
                Format: "url",
                Description: "Company website URL"
            } as IFieldDefinition;
            properties[appCode + "CompanyPhone"] = {
                Title: "Phone",
                Type: "string",
                Description: "Company phone number"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Companies",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildCompaniesList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildContactsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ContactName"] = {
                Title: "Contact Name",
                Type: "string",
                Description: "Full name of the contact",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "Company"] = {
                Title: "Company",
                Type: "lookup",
                Description: "Associated company",
                LookupList: "Companies",
                LookupField: appCode + "CompanyName"
            } as IFieldDefinition;
            properties[appCode + "ContactEmail"] = {
                Title: "Email",
                Type: "string",
                Format: "email",
                Description: "Contact email address"
            } as IFieldDefinition;
            properties[appCode + "ContactPhone"] = {
                Title: "Phone",
                Type: "string",
                Description: "Contact phone number"
            } as IFieldDefinition;
            properties[appCode + "JobTitle"] = {
                Title: "Job Title",
                Type: "string",
                Description: "Contact job title"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Contacts",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildContactsList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildOpportunitiesList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "OpportunityName"] = {
                Title: "Opportunity Name",
                Type: "string",
                Description: "Name of the opportunity",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "OpportunityCompany"] = {
                Title: "Company",
                Type: "lookup",
                Description: "Associated company",
                LookupList: "Companies",
                LookupField: appCode + "CompanyName"
            } as IFieldDefinition;
            properties[appCode + "Value"] = {
                Title: "Value",
                Type: "number",
                Description: "Estimated deal value"
            } as IFieldDefinition;
            properties[appCode + "Stage"] = {
                Title: "Stage",
                Type: "string",
                Description: "Current deal stage",
                Enum: ["Prospecting", "Qualification", "Proposal", "Negotiation", "Closed Won", "Closed Lost"]
            } as IFieldDefinition;
            properties[appCode + "CloseDate"] = {
                Title: "Close Date",
                Type: "string",
                Format: "date",
                Description: "Expected close date"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Opportunities",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 750, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildOpportunitiesList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Inventory Template

    public static GetInventoryTemplate = (): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const appCode: string = DBState.GetAppCode();
            const items: IListDefinition = DBTemplateManager.BuildInventoryItemsList(appCode);
            const locations: IListDefinition = DBTemplateManager.BuildLocationsList(appCode);
            const movements: IListDefinition = DBTemplateManager.BuildStockMovementsList(appCode);
            return [items, locations, movements];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetInventoryTemplate");
            return [];
        }
    };

    private static BuildInventoryItemsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "ItemName"] = {
                Title: "Item Name",
                Type: "string",
                Description: "Name of the inventory item",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ItemSKU"] = {
                Title: "SKU",
                Type: "string",
                Description: "Stock Keeping Unit",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "ItemDescription"] = {
                Title: "Description",
                Type: "string",
                Format: "note",
                Description: "Item description"
            } as IFieldDefinition;
            properties[appCode + "UnitCost"] = {
                Title: "Unit Cost",
                Type: "number",
                Description: "Cost per unit"
            } as IFieldDefinition;
            properties[appCode + "ReorderLevel"] = {
                Title: "Reorder Level",
                Type: "integer",
                Description: "Minimum stock level before reorder"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Items",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 50, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildInventoryItemsList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildLocationsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "LocationName"] = {
                Title: "Location Name",
                Type: "string",
                Description: "Name of the storage location",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "LocationType"] = {
                Title: "Location Type",
                Type: "string",
                Description: "Type of location",
                Enum: ["Warehouse", "Store", "Distribution Center"]
            } as IFieldDefinition;
            properties[appCode + "LocationAddress"] = {
                Title: "Address",
                Type: "string",
                Format: "note",
                Description: "Physical address"
            } as IFieldDefinition;
            properties[appCode + "Capacity"] = {
                Title: "Capacity",
                Type: "integer",
                Description: "Maximum storage capacity"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "Locations",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 400, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildLocationsList");
            return null as unknown as IListDefinition;
        }
    };

    private static BuildStockMovementsList = (appCode: string): IListDefinition => {
        if (ks.kErrorMode === true) { return null as unknown as IListDefinition; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            properties[appCode + "MovementItem"] = {
                Title: "Item",
                Type: "lookup",
                Description: "Item being moved",
                LookupList: "Items",
                LookupField: appCode + "ItemName",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "FromLocation"] = {
                Title: "From Location",
                Type: "lookup",
                Description: "Source location",
                LookupList: "Locations",
                LookupField: appCode + "LocationName"
            } as IFieldDefinition;
            properties[appCode + "ToLocation"] = {
                Title: "To Location",
                Type: "lookup",
                Description: "Destination location",
                LookupList: "Locations",
                LookupField: appCode + "LocationName"
            } as IFieldDefinition;
            properties[appCode + "MovementQuantity"] = {
                Title: "Quantity",
                Type: "integer",
                Description: "Number of units moved",
                Required: true
            } as IFieldDefinition;
            properties[appCode + "MovementType"] = {
                Title: "Movement Type",
                Type: "string",
                Description: "Type of stock movement",
                Enum: ["Transfer", "Receipt", "Dispatch", "Adjustment", "Return"]
            } as IFieldDefinition;
            properties[appCode + "MovementDate"] = {
                Title: "Movement Date",
                Type: "string",
                Format: "date",
                Description: "Date of the movement"
            } as IFieldDefinition;
            return {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: "StockMovements",
                ListType: "list",
                ListTemplate: 100,
                LibraryType: "",
                Properties: properties,
                Data: [],
                Views: [],
                Settings: null,
                Position: { X: 750, Y: 50 },
                Collapsed: false,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: ""
            } as IListDefinition;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.BuildStockMovementsList");
            return null as unknown as IListDefinition;
        }
    };

    //#endregion

    //#region Save Template

    public static OpenSaveTemplateModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.Lists.length === 0) {
                DBModalManager.ShowToast("No lists to save as template", "error");
                return;
            }
            const nameInput: HTMLInputElement | null = document.getElementById("templateName") as HTMLInputElement | null;
            if (nameInput !== null) {
                nameInput.value = "";
            }
            const descInput: HTMLTextAreaElement | null = document.getElementById("templateDescription") as HTMLTextAreaElement | null;
            if (descInput !== null) {
                descInput.value = "";
            }
            DBModalManager.OpenModal("saveTemplateModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.OpenSaveTemplateModal");
        }
    };

    public static SaveAsTemplate = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const nameInput: HTMLInputElement | null = document.getElementById("templateName") as HTMLInputElement | null;
            const descInput: HTMLTextAreaElement | null = document.getElementById("templateDescription") as HTMLTextAreaElement | null;
            const name: string = nameInput !== null ? nameInput.value.trim() : "";
            const description: string = descInput !== null ? descInput.value.trim() : "";
            if (name === "") {
                DBModalManager.ShowToast("Please enter a template name", "error");
                return;
            }
            const template: Record<string, unknown> = {
                name: name,
                description: description,
                created: new Date().toISOString(),
                lists: DBState.Lists,
                appCode: DBState.GetAppCode()
            };
            const templates: unknown[] = DBTemplateManager.GetUserTemplates();
            templates.push(template);
            localStorage.setItem("userTemplates", JSON.stringify(templates));
            DBModalManager.CloseModal("saveTemplateModal");
            DBModalManager.ShowToast("Template saved successfully", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.SaveAsTemplate");
        }
    };

    //#endregion

    //#region User Templates

    public static GetUserTemplates = (): any[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const saved: string | null = localStorage.getItem("userTemplates");
            if (saved !== null) {
                return JSON.parse(saved);
            }
            return [];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetUserTemplates");
            return [];
        }
    };

    //#endregion

    //#region Template Modal

    public static OpenTemplateModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBTemplateManager.RenderBuiltInTemplates();
            DBTemplateManager.RenderUserTemplates();
            DBModalManager.OpenModal("templateModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.OpenTemplateModal");
        }
    };

    public static RenderBuiltInTemplates = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("builtInTemplates");
            if (container === null) {
                return;
            }
            const builtInTemplates: Record<string, unknown>[] = DBTemplateManager.GetBuiltInTemplates();
            let html: string = "";
            const buildCard = (template: Record<string, unknown>, index: number): string => {
                const name: string = DBStringUtils.EscapeHtml(template.name as string);
                const description: string = DBStringUtils.EscapeHtml(template.description as string);
                const lists: unknown[] = template.lists as unknown[];
                const listCount: number = lists.length;
                return "<div class=\"template-card\" role=\"button\" tabindex=\"0\" data-action=\"load-built-in-template\" data-template-index=\"" + index + "\" aria-label=\"Load template " + name + "\">" +
                    "<div class=\"template-card-header\">" +
                    "<h4>" + name + "</h4>" +
                    "<span class=\"badge\">" + listCount + " list(s)</span>" +
                    "</div>" +
                    "<p>" + description + "</p>" +
                    "</div>";
            };
            const appendCard = (template: Record<string, unknown>, index: number): void => {
                html += buildCard(template, index);
            };
            builtInTemplates.forEach(appendCard);
            container.innerHTML = html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.RenderBuiltInTemplates");
        }
    };

    public static RenderUserTemplates = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("userTemplates");
            if (container === null) {
                return;
            }
            const section: HTMLElement | null = container.parentElement;
            const userTemplates: any[] = DBTemplateManager.GetUserTemplates();
            if (userTemplates.length === 0) {
                container.innerHTML = "";
                if (section !== null) { section.style.display = "none"; }
                return;
            }
            if (section !== null) { section.style.display = ""; }
            let html: string = "";
            const buildUserCard = (template: Record<string, unknown>, index: number): string => {
                const name: string = DBStringUtils.EscapeHtml(template.name as string);
                const description: string = DBStringUtils.EscapeHtml(template.description as string);
                const lists: unknown[] = template.lists as unknown[];
                const listCount: number = lists.length;
                const created: string = template.created as string;
                const savedDate: string = new Date(created).toLocaleDateString();
                return "<div class=\"template-card\" role=\"button\" tabindex=\"0\" data-action=\"load-user-template\" data-template-index=\"" + index + "\" aria-label=\"Load template " + name + "\">" +
                    "<button class=\"template-card-delete\" data-action=\"delete-user-template\" data-template-index=\"" + index + "\" aria-label=\"Delete template " + name + "\" title=\"Delete\">×</button>" +
                    "<div class=\"template-card-header\">" +
                    "<h4>" + name + "</h4>" +
                    "<span class=\"badge\">" + listCount + " list(s)</span>" +
                    "</div>" +
                    "<p>" + description + "</p>" +
                    "<p class=\"template-date\">Saved: " + DBStringUtils.EscapeHtml(savedDate) + "</p>" +
                    "</div>";
            };
            const appendUserCard = (template: Record<string, unknown>, index: number): void => {
                html += buildUserCard(template, index);
            };
            userTemplates.forEach(appendUserCard);
            container.innerHTML = html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.RenderUserTemplates");
        }
    };

    //#endregion

    //#region Load Template

    public static LoadTemplate = (type: string, index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            let template: Record<string, unknown> | null = null;
            if (type === "built-in") {
                const builtIn: Record<string, unknown>[] = DBTemplateManager.GetBuiltInTemplates();
                if (index >= 0 && index < builtIn.length) {
                    template = builtIn[index];
                }
            } else {
                const userTemplates: any[] = DBTemplateManager.GetUserTemplates();
                if (index >= 0 && index < userTemplates.length) {
                    template = userTemplates[index];
                }
            }
            if (template === null) {
                DBModalManager.ShowToast("Template not found", "error");
                return;
            }
            DBTemplateManager.ApplyTemplate(template);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.LoadTemplate");
        }
    };

    private static ApplyTemplate = (template: Record<string, unknown>): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const lists: IListDefinition[] = JSON.parse(JSON.stringify(template.lists));
            DBState.Lists = lists;
            DBListManager.CollapseAllCards();
            DBState.SaveState();
            DBModalManager.CloseModal("templateModal");
            DBListManager.AutoArrangeLists();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.ApplyTemplate");
        }
    };

    //#endregion

    //#region Delete Template

    public static DeleteTemplate = (index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const templates: any[] = DBTemplateManager.GetUserTemplates();
            if (index < 0 || index >= templates.length) {
                DBModalManager.ShowToast("Template not found", "error");
                return;
            }
            const templateName: string = templates[index].name || "this template";
            const capturedIndex: number = index;
            const onConfirmDelete = (): void => {
                DBTemplateManager.DeleteTemplateConfirmed(capturedIndex);
            };
            DBModalManager.ShowConfirmation({
                title: "Delete Template",
                message: "Are you sure you want to delete \"" + templateName + "\"? This action cannot be undone.",
                confirmText: "DELETE",
                confirmLabel: "Delete Template",
                onConfirm: onConfirmDelete
            });
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.DeleteTemplate");
        }
    };

    private static DeleteTemplateConfirmed = (index: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const templates: any[] = DBTemplateManager.GetUserTemplates();
            templates.splice(index, 1);
            localStorage.setItem("userTemplates", JSON.stringify(templates));
            DBTemplateManager.RenderUserTemplates();
            DBModalManager.ShowToast("Template deleted successfully", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.DeleteTemplateConfirmed");
        }
    };

    //#endregion

    //#region Get Built-In Templates

    public static GetBuiltInTemplates = (): Record<string, unknown>[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            return [
                {
                    name: "E-commerce (Full Example)",
                    description: "Complete e-commerce with 7 lists, lookup relationships, and sample data.",
                    lists: DBTemplateManager.GetExampleEcommerceTemplate()
                },
                {
                    name: "E-commerce",
                    description: "Basic e-commerce schema with categories, products, and customers.",
                    lists: DBTemplateManager.GetEcommerceTemplate()
                },
                {
                    name: "Project Management",
                    description: "Project and task tracking with status workflows and assignments.",
                    lists: DBTemplateManager.GetProjectManagementTemplate()
                },
                {
                    name: "CRM",
                    description: "Customer relationship management with companies, contacts, and opportunities.",
                    lists: DBTemplateManager.GetCRMTemplate()
                },
                {
                    name: "Inventory",
                    description: "Inventory management with items, locations, and stock movements.",
                    lists: DBTemplateManager.GetInventoryTemplate()
                }
            ];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetBuiltInTemplates");
            return [];
        }
    };

    private static GetExampleEcommerceTemplate = (): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const appCode: string = DBState.GetAppCode();
            return [
                DBTemplateManager.BuildCategoriesList(appCode),
                DBTemplateManager.BuildSuppliersList(appCode),
                DBTemplateManager.BuildProductsList(appCode),
                DBTemplateManager.BuildCustomersList(appCode),
                DBTemplateManager.BuildOrdersList(appCode),
                DBTemplateManager.BuildOrderItemsList(appCode),
                DBTemplateManager.BuildProductReviewsList(appCode)
            ];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBTemplateManager.GetExampleEcommerceTemplate");
            return [];
        }
    };

    //#endregion
}

export default DBTemplateManager;
